/**
 * Hands shipping labels / packing slips to the Riverr Direct Print desktop app
 * via the custom `riverr:` protocol.
 *
 * Launch in a hidden named iframe so Ship Now does not open an `about:blank`
 * tab. Never use window.open(..., "_blank") — Chrome leaves that tab on
 * about:blank after the OS takes the protocol, and close() often cannot
 * dismiss it.
 */

export const PRINT_APP_PROTOCOL = "riverr";
export const PRINT_APP_INSTALL_DISMISS_KEY = "riverr:print-app-install-dismissed";
export const PRINT_APP_INSTALL_SESSION_KEY =
  "riverr:print-app-install-prompted";
export const PRINT_APP_DOWNLOAD_URL =
  "https://storage.googleapis.com/riverr-enterprise-user.appspot.com/users%2FR7UPQvH1cOUPPHcgOVtcaqRycAA3%2Fapps%2F1669053987910Riverr-Direct-Print.zip";
export const PRINT_APP_HELP_URL =
  "https://help.riverr.app/en/understanding-the-production-workflow/direct-print-application";
export const PRINT_APP_INTEGRATIONS_PATH = "/integrations";
export const PRINT_APP_FRAME_NAME = "riverr-direct-print";

/** Wait before deciding the desktop app may be missing. */
const INSTALL_DETECT_MS = 2500;

let promptListener = null;
let enterpriseInstallDismissed = false;

export const setEnterprisePrintAppInstallDismissed = (dismissed) => {
  enterpriseInstallDismissed = Boolean(dismissed);
  if (enterpriseInstallDismissed) {
    try {
      window.localStorage.setItem(PRINT_APP_INSTALL_DISMISS_KEY, "1");
    } catch {
      /* ignore quota / private mode */
    }
  }
};

export const isPrintAppInstallDismissed = () => {
  if (enterpriseInstallDismissed) return true;
  try {
    return window.localStorage.getItem(PRINT_APP_INSTALL_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

export const dismissPrintAppInstallPrompt = () => {
  enterpriseInstallDismissed = true;
  try {
    window.localStorage.setItem(PRINT_APP_INSTALL_DISMISS_KEY, "1");
  } catch {
    /* ignore quota / private mode */
  }
};

export const clearPrintAppInstallDismiss = () => {
  try {
    window.localStorage.removeItem(PRINT_APP_INSTALL_DISMISS_KEY);
  } catch {
    /* ignore */
  }
};

export const wasPrintAppInstallPromptedThisSession = () => {
  try {
    return window.sessionStorage.getItem(PRINT_APP_INSTALL_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const markPrintAppInstallPromptedThisSession = () => {
  try {
    window.sessionStorage.setItem(PRINT_APP_INSTALL_SESSION_KEY, "1");
  } catch {
    /* ignore quota / private mode */
  }
};

/**
 * Hidden-iframe launches rarely steal focus (tray apps don't either), so
 * "app missing" detection false-positives on almost every Create Label.
 * Prompt at most once per tab session unless the user already opted out.
 */
export const shouldPromptPrintAppInstall = () =>
  !isPrintAppInstallDismissed() && !wasPrintAppInstallPromptedThisSession();

/** Register the UI that should open when the desktop app appears missing. */
export const registerPrintAppInstallPrompt = (listener) => {
  promptListener = typeof listener === "function" ? listener : null;
  return () => {
    if (promptListener === listener) promptListener = null;
  };
};

export const buildPrintAppUrl = (maskedOrderId) =>
  `${PRINT_APP_PROTOCOL}:orderid=${maskedOrderId}`;

const ensureHiddenFrame = () => {
  let iframe = document.querySelector(`iframe[name="${PRINT_APP_FRAME_NAME}"]`);
  if (iframe) return iframe;
  iframe = document.createElement("iframe");
  iframe.name = PRINT_APP_FRAME_NAME;
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  iframe.style.cssText =
    "position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none";
  document.body.appendChild(iframe);
  return iframe;
};

/**
 * Target the hidden iframe. window.open with that name is still a user-gesture
 * protocol launch, but it does not create a visible tab.
 */
export const launchPrintAppUrl = (url) => {
  const iframe = ensureHiddenFrame();
  try {
    window.open(url, PRINT_APP_FRAME_NAME);
  } catch {
    iframe.src = url;
  }
  return iframe;
};

/**
 * Try to open the Direct Print app for a masked order id.
 * Resolves with `{ launched }` once detection settles.
 */
export const openPrintApp = (maskedOrderId) => {
  return new Promise((resolve) => {
    if (!maskedOrderId) {
      resolve({ launched: false });
      return;
    }

    const url = buildPrintAppUrl(maskedOrderId);
    let settled = false;
    let sawFocusLoss = false;

    const cleanupListeners = () => {
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
    };

    const finish = (didLaunch) => {
      if (settled) return;
      settled = true;
      cleanupListeners();

      if (!didLaunch && shouldPromptPrintAppInstall() && promptListener) {
        markPrintAppInstallPromptedThisSession();
        promptListener();
      }

      resolve({ launched: didLaunch });
    };

    const onBlur = () => {
      sawFocusLoss = true;
      finish(true);
    };

    const onVisibility = () => {
      if (document.hidden) {
        sawFocusLoss = true;
        finish(true);
      }
    };

    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);

    launchPrintAppUrl(url);

    setTimeout(() => {
      finish(sawFocusLoss);
    }, INSTALL_DETECT_MS);
  });
};

/**
 * A full reload in the same turn destroys the hidden frame before the OS
 * accepts `riverr:`. Callers that navigate away (QC Ship Now) must await this.
 * Launching only after `?autoprint=1` has no click, and Chrome blocks it.
 */
export const PRINT_APP_HANDOFF_MS = 2000;

export function holdForPrintHandoff() {
  return new Promise((resolve) => {
    setTimeout(resolve, PRINT_APP_HANDOFF_MS);
  });
}

export async function handoffPrintApp(maskedOrderId) {
  if (!maskedOrderId) return { launched: false };
  const pending = openPrintApp(maskedOrderId);
  const [result] = await Promise.all([pending, holdForPrintHandoff()]);
  return result;
}
